import { createBrowserRouter, RouterProvider } from "react-router";
import "@fontsource/inter/300.css";
import "@fontsource/inter/400.css";
import "@fontsource/inter/500.css";
import "@fontsource/inter/600.css";
import "@fontsource/inter/700.css";
import { ThemeProvider } from "@accredo/fe-shared/theme/index.js";
import { PORTAL } from "@accredo/fe-shared/theme/portals.js";
import { LocalizationProvider } from "@mui/x-date-pickers";
import { AdapterDayjs } from "@mui/x-date-pickers/AdapterDayjs";
import "dayjs/locale/de";
import { SnackbarProvider } from "notistack";
import { getEnv } from "@accredo/fe-shared/env.js";
import { UserProvider } from "@accredo/fe-shared/contexts/UserContextProvider.jsx";
import { BreadcrumbProvider } from "@accredo/fe-shared/contexts/BreadcrumbContext.jsx";
import { ConfirmProvider } from "@accredo/fe-shared/contexts/ConfirmContext.jsx";
import { appRoutes } from "./portalRoutes.jsx";
import { useEffect, useMemo } from "react";

/**
 * Main App component of the Kundenportal.
 *
 * Which portal this is is decided at build time (this is the Kundenportal
 * app), not by inspecting the hostname at runtime.
 */
export default function App() {
  useEffect(() => {
    document.title = getEnv("VITE_KUNDENPORTAL_TITLE") || "Kundenportal";
    const favicon = getEnv("VITE_TENANT_FAVICON") || "disphere_favicon.png";
    const link = document.createElement("link");
    link.rel = "icon";
    link.href = "/" + favicon;
    document.head.appendChild(link);

    return () => {
      document.head.removeChild(link);
    };
  }, []);

  const router = useMemo(() => createBrowserRouter(appRoutes), []);

  return (
    <ThemeProvider portal={PORTAL.KUNDENPORTAL}>
      <LocalizationProvider dateAdapter={AdapterDayjs} adapterLocale="de">
        <SnackbarProvider
          maxSnack={3}
          anchorOrigin={{
            vertical: "bottom",
            horizontal: "right",
          }}
        >
          <UserProvider>
            <BreadcrumbProvider>
              <ConfirmProvider>
                <RouterProvider router={router} />
              </ConfirmProvider>
            </BreadcrumbProvider>
          </UserProvider>
        </SnackbarProvider>
      </LocalizationProvider>
    </ThemeProvider>
  );
}
